import { auth } from "../auth/FirebaseAuth";
import { signInWithEmailAndPassword, sendSignInLinkToEmail, ActionCodeSettings, getMultiFactorResolver, TotpMultiFactorGenerator } from "firebase/auth";
import { getFunctions, httpsCallable } from "firebase/functions";
import { getDatabase, ref, set, get, child, push, remove } from "firebase/database";
import navigationConfig from "configs/NavigationConfig";

const additionalPerms = ['delete-graylist'];
const allViews = navigationConfig.map((menu) => {
    if (menu.submenu.length > 0)
        return menu.submenu.map((subMenuFirst) => subMenuFirst.key)
    else
      return menu.key;
}).flat().concat(additionalPerms);

export type usersData = {
  [userId: string]: {
    roles: {
      [roleId: string]: string;
    },
    email: string,
    views?: string[]
  };
}

export type roleData = {
  [roleId: string]: {
    perms: string[],
    name: string,
    color: string
  }
}

// Funções auxiliares para hash SHA-256 dos códigos de recuperação
const hashRecoveryCode = async (code: string): Promise<string> => {
  const encoder = new TextEncoder();
  const data = encoder.encode(code + "recovery_salt_2025");
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
};

const hashRecoveryCodes = async (codes: string[]): Promise<string[]> => {
  return Promise.all(codes.map(code => hashRecoveryCode(code)));
};

const FirebaseService = {
  signInEmailWithPasswordRequest: (email: string, password: string) => {
    return signInWithEmailAndPassword(auth, email, password)
      .then(function (userCredential) { return userCredential; })
      .catch(function (error) {
        if (error.code === 'auth/multi-factor-auth-required') {
          const resolver = getMultiFactorResolver(auth, error);

          return {
            needsMfa: true,
            resolver: resolver,
            message: "Autenticação MFA necessária",
            email: email
          };
        }
        else if (error.code === 'auth/wrong-password') {
          return { message: "Senha incorreta. Tente novamente." };
        }
        else if (error.code === 'auth/user-not-found') {
          return { message: "Usuário não encontrado. Verifique o email e tente novamente." };
        }
        return { message: error.message };
      });
  },

  signInEmailWithLinkRequest: (email: string, redirectUrl: string) => {
    const settings: ActionCodeSettings = { url: redirectUrl, handleCodeInApp: true };
    return sendSignInLinkToEmail(auth, email, settings);
  },

  signOutRequest: () => auth.signOut().catch((err) => err),

  resolveMfaRequest: (resolver: any, verificationCode: string) => {
    try {
      const multiFactorAssertion = TotpMultiFactorGenerator.assertionForSignIn(
        resolver.hints[0].uid,
        verificationCode
      );

      return resolver.resolveSignIn(multiFactorAssertion)
        .then((userCredential: any) => userCredential)
        .catch((error: any) => {
          if (error.code === 'auth/invalid-verification-code') {
            return { message: "Código inválido. Verifique o código gerado no seu aplicativo autenticador." };
          }
          return { message: error.message };
        });
    } catch (error: any) {
      return Promise.resolve({ message: error.message });
    }
  },

  currentUser: () => auth.currentUser,

  createRole: async (name: string, perms: string[], color: string): Promise<string | null> => {
    try {
      const dbRef = ref(getDatabase(), 'roles/');
      const pushKey = push(dbRef).key;
      await set(child(dbRef, pushKey), { name, color, perms });
      return pushKey;
    } catch (err) {
      return null;
    }
  },

  editRole: async (roleId: string, name: string, perms: string[], color: string): Promise<boolean> => {
    try {
      const dbRef = getDatabase();
      await set(
        ref(dbRef, 'roles/' + roleId),
        { name, color, perms }
      );
      return true;
    } catch (err) {
      return false;
    }
  },

  deleteRole: async (role: string): Promise<boolean> => {
    try {
      const dbRef = getDatabase();
      await remove(ref(dbRef, `roles/${role}`));
      const users: usersData = (await get(ref(dbRef, 'users/'))).val();
      const updates: string[] = [];

      Object.entries(users).forEach(([userId, userData]) => {
        const roles = userData.roles || {};

        Object.entries(roles).forEach(([roleId, roleData]) => {
          if (roleData === role) {
            updates.push(`users/${userId}/roles/${roleId}`)
          }
        })
      });

      if (updates.length > 0) {
        updates.map(async (update) => {
          await remove(ref(dbRef, update));
        })
      }
      return true;
    } catch (err) {
      return false;
    }
  },

  getRoles: async () => {
    const dbRef = getDatabase();
    const snapshot = await get(ref(dbRef, 'roles/'))
    return snapshot.val();
  },

  getSelfPermissionsAndViews: async () => {
    let dbRef = ref(getDatabase(), 'users/');
    const roleRef = ref(getDatabase(), 'roles/');

    if (auth.currentUser)
      dbRef = child(dbRef, auth.currentUser.uid);
    else
      return [['home'], allViews];

    const user = (await get(dbRef)).val();
    if (user === null) {
      await set(dbRef, {
        email: auth.currentUser.email,
        roles: { '0': 'default' },
        views: allViews
      });
      return [['home'], allViews];
    }

    const views = user.views;
    const userRoles = user.roles;
    const profValues: string[] = Object.values(userRoles)

    if(profValues.includes('admin')){
      // Para se dar todas as permissões, basta por esse retorno fora do if
      return [allViews, views];
    }

    const roles: roleData = (await get(roleRef)).val();
    let perms: string[] = []

    profValues.forEach(p => {
      perms = perms.concat(roles[p].perms);
    });

    perms = [...new Set(perms)];
    return [perms, views];
  },

  // Retorna os nomes amigáveis dos cargos do usuário logado (para segmentação de telemetria).
  getSelfRoleNames: async (): Promise<string[]> => {
    try {
      if (!auth.currentUser) return [];

      const userSnap = await get(child(ref(getDatabase(), 'users/'), auth.currentUser.uid));
      const user = userSnap.val();
      if (!user || !user.roles) return [];

      const roleIds: string[] = Object.values(user.roles);
      const rolesSnap = await get(ref(getDatabase(), 'roles/'));
      const roles: roleData = rolesSnap.val() || {};

      return roleIds.map((id) => (id === 'admin' ? 'admin' : roles[id]?.name || id));
    } catch (err) {
      return [];
    }
  },

  addRoleToUser: async (uid: string, role: string): Promise<string | null> => {
    if (role === 'admin') return null;

    try {
      const dbRef = ref(getDatabase(), 'users/' + uid + '/roles');
      const pushKey = push(dbRef).key;
      await set(child(dbRef, pushKey), role);
      return pushKey;
    } catch (err) {
      return null;
    }
  },

  removeRoleOfUser: async (uid: string, role: string): Promise<boolean> => {
    if (role === 'admin') return false;

    const dbRef = getDatabase();
    const rolesRef = ref(dbRef, 'users/' + uid + '/roles');
    const roles = (await get(rolesRef)).val();

    if (!roles) {
      console.log("Usuário não encontrado");
      return false;
    }

    const keyToDelete = Object.keys(roles).find(
      (key) => roles[key] === role
    );

    if (keyToDelete) {
      try {
        remove(child(rolesRef, keyToDelete));
        return true;
      } catch (err) {
        return false;
      }
    } else {
      console.log("Cargo não encontrado");
      return false;
    }
  },

  listUsers: async () => {
    const dbRef = getDatabase();
    const users = (await get(ref(dbRef, 'users/'))).val();
    return users;
  },

  addUser: async (uid: string, email: string) => {
    const dbRef = getDatabase();
    await set(
      ref(dbRef, 'users/' + uid),
      { email, roles: { '0': 'default' } }
    );
  },

  setSelfViews: async (views: string[]): Promise<boolean> => {
    let uid = '';
    if (auth.currentUser)
      uid = auth.currentUser.uid;
    else {
      console.log('Usuario não logado');
      return false;
    }

    try {
      const dbRef = getDatabase();
      await set(ref(dbRef, 'users/' + uid + '/views'), views);
      return true;
    } catch (err) {
      return false;
    }
  },

  addRecoveryCodesToUser: async (uid: string, recoveryCodes: string[]): Promise<boolean> => {
    try {
      const dbRef = getDatabase();
      // Gera hash dos códigos antes de salvar no banco
      const hashedCodes = await hashRecoveryCodes(recoveryCodes);
      await set(ref(dbRef, 'users/' + uid + '/recoveryCodes'), hashedCodes);
      return true;
    } catch (err) {
      console.error('Erro ao salvar códigos de recuperação:', err);
      return false;
    }
  },

  removeMfaFromUser: (email: string) => {
    const functions = getFunctions();
    const disableMFA = httpsCallable(functions, 'disableMFA');
    return disableMFA({ email })
      .then((result) => {
        return result.data;
      })
      .catch((error) => {
        return { message: error.message };
      });
  },

  // Função para verificar código de recuperação via Cloud Function
  verifyRecoveryCodeByEmail: async (email: string, recoveryCode: string) => {
    const functions = getFunctions();
    const verifyRecoveryCode = httpsCallable(functions, 'verifyRecoveryCode');
    return verifyRecoveryCode({ email, recoveryCode })
      .then((result) => {
        const data = result.data as { success: boolean; message: string };
        return { success: data.success, message: data.message };
      })
      .catch((error) => {
        console.error('Erro ao verificar código de recuperação:', error);
        return { success: false, message: error.message };
      });
  }
};

export default FirebaseService;
